/**
 * Browser-only viewport handling for the CDN-served web build
 * (app.streetdicelegends.com). Presents the game in portrait and attempts
 * fullscreen / orientation lock.
 *
 * IMPORTANT: this is a NO-OP inside the Capacitor native shell. iOS and Android
 * builds keep their own native orientation + immersive config (AndroidManifest
 * screenOrientation, Info.plist UISupportedInterfaceOrientations, StatusBar
 * plugin) and are completely untouched by anything here.
 *
 * Browser reality this works around:
 *  - iOS Safari cannot lock orientation or use the Fullscreen API, so the
 *    cross-browser-reliable piece is the "rotate to portrait" overlay shown in
 *    landscape. Real orientation lock + chrome-hiding fullscreen work on
 *    Android Chrome (gesture-gated). Desktop gets a centered portrait frame.
 */

import { Capacitor } from '@capacitor/core';
import { isMobilePlatform } from './platform';

let initialized = false;

export function initWebViewport(): void {
  if (initialized) return;
  // Native builds manage orientation/fullscreen natively. Leave them alone.
  if (Capacitor.isNativePlatform()) return;
  initialized = true;

  const mobile = isMobilePlatform();
  document.body.classList.add('platform-web', mobile ? 'web-mobile' : 'web-desktop');

  injectStyles();

  if (mobile) {
    addRotateOverlay();
    setupFullscreenOnGesture();
  }

  // The desktop portrait frame shrinks #app; nudge the renderer to re-read the
  // (now smaller) container size on the next frame.
  requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
}

function injectStyles(): void {
  const css = `
    /* Desktop browser: frame the portrait game centered, letterboxed in black. */
    body.platform-web.web-desktop { background: #000; }
    body.platform-web.web-desktop #app {
      width: min(100vw, calc(100vh * 9 / 16));
      width: min(100vw, calc(100dvh * 9 / 16));
      height: 100vh;
      height: 100dvh;
      margin: 0 auto;
      box-shadow: 0 0 60px rgba(0, 0, 0, 0.8);
    }

    /* Mobile browser in landscape: prompt to rotate (only reliable way to
       "force" portrait when the browser cannot lock orientation). */
    #rotate-overlay {
      position: fixed; inset: 0; z-index: 99999;
      display: none;
      flex-direction: column; align-items: center; justify-content: center;
      text-align: center; padding: 32px;
      background: #0a0a0a; color: #fff;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    #rotate-overlay .icon {
      font-size: 56px; margin-bottom: 20px;
      animation: sdlRotateHint 1.8s ease-in-out infinite;
    }
    #rotate-overlay h2 { font-size: 20px; font-weight: 700; margin-bottom: 10px; }
    #rotate-overlay p { font-size: 14px; line-height: 1.5; color: rgba(255, 255, 255, 0.6); max-width: 260px; }
    @keyframes sdlRotateHint {
      0%, 40% { transform: rotate(0deg); }
      60%, 100% { transform: rotate(-90deg); }
    }
    @media (orientation: landscape) {
      body.platform-web.web-mobile #rotate-overlay { display: flex; }
    }
  `;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}

function addRotateOverlay(): void {
  const o = document.createElement('div');
  o.id = 'rotate-overlay';
  o.innerHTML = `
    <div class="icon">📱</div>
    <h2>Rotate your device</h2>
    <p>Street Dice Legends plays in portrait. Turn your phone upright to keep going.</p>
  `;
  document.body.appendChild(o);
}

function setupFullscreenOnGesture(): void {
  // Fullscreen + orientation lock both require a user gesture. Fire once on the
  // first touch; both are best-effort and silently no-op where unsupported
  // (notably iOS Safari).
  const tryImmersive = () => {
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      try {
        Promise.resolve(req.call(el)).then(lockPortrait).catch(lockPortrait);
      } catch {
        lockPortrait();
      }
    } else {
      lockPortrait();
    }
  };
  window.addEventListener('pointerdown', tryImmersive, { once: true });
}

function lockPortrait(): void {
  const orientation = (screen as Screen & {
    orientation?: { lock?: (o: string) => Promise<void> };
  }).orientation;
  if (orientation && typeof orientation.lock === 'function') {
    try {
      Promise.resolve(orientation.lock('portrait')).catch(() => {});
    } catch {
      /* unsupported — the rotate overlay covers this case */
    }
  }
}
